import { ComponentType, lazy } from 'react'
import { Brand } from '../../../../../server/src/user/user.contract'

// Cache for loaded layout components
const layoutCache = new Map<string, ComponentType<any>>()

/**
 * Dynamically loads a brand-specific layout wrapper component with fallback to default
 * @param brand - The brand to load layout for
 * @returns Promise resolving to the layout wrapper component
 */
async function getBrandLayout(
  brand: Brand = Brand.default,
): Promise<ComponentType<any>> {
  const cacheKey = `layout:${brand}`

  if (layoutCache.has(cacheKey)) {
    return layoutCache.get(cacheKey)!
  }

  try {
    // Try to load brand-specific layout wrapper
    const module = await import(`./${brand}/layout/layout-wrapper.tsx`)
    const component = module.default
    layoutCache.set(cacheKey, component)
    return component
  } catch (error) {
    // Fallback to default layout wrapper
    if (brand !== Brand.default) {
      console.warn(
        `Brand-specific layout for "${brand}" not found, falling back to default`,
      )
      return getBrandLayout(Brand.default)
    }
    throw error
  }
}

/**
 * Creates a lazy-loaded layout wrapper component for a specific brand
 * @param brand - The brand to create layout for
 * @returns Lazy-loaded React component
 */
export function createLazyLayout(brand: Brand) {
  return lazy(() =>
    getBrandLayout(brand).then((component) => ({ default: component })),
  )
}
